import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';

export type GlobalAssistantEntityType =
  | 'company'
  | 'geography'
  | 'role'
  /** Standalone `/talent-discovery` page (no primary company/geo/role filter). */
  | 'talentDiscovery';
export type GlobalAssistantContextPolicy = 'full' | 'recent_only' | 'none';

export type CustomDashAssistantEntityType = 'company' | 'geography' | 'role';

export type GlobalAssistantDiscoveryKind = 'talent' | 'company';

export type GlobalAssistantDebugTiming = {
  label: string;
  ms: number;
  model?: string;
};

export type GlobalAssistantWarmUpResponse = {
  status: 'started' | 'in_flight' | 'already_warm';
  model?: string;
  ttlMs?: number;
  warmedAgeMs?: number;
};

export function isCustomDashAssistantEntityType(
  entityType: GlobalAssistantEntityType | undefined
): entityType is CustomDashAssistantEntityType {
  return (
    entityType === 'company' ||
    entityType === 'geography' ||
    entityType === 'role'
  );
}

export function toCustomDashAssistantEntityType(
  entityType: GlobalAssistantEntityType | undefined
): CustomDashAssistantEntityType | undefined {
  if (
    entityType === 'company' ||
    entityType === 'geography' ||
    entityType === 'role'
  ) {
    return entityType;
  }
  return undefined;
}

export type GlobalAssistantSuggestMatch = {
  pageId: string;
  name: string;
  navigatePath: string;
  confidenceScore: number;
  applyFiltersAfterNavigate?: boolean;
  discoveryKind?: GlobalAssistantDiscoveryKind;
  resolvedPromptForApply?: string;
};

export type GlobalAssistantConversationTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export type GlobalAssistantCurrentPageContext =
  | {
      pathname: string;
      kind: 'discovery';
      discoveryKind: GlobalAssistantDiscoveryKind;
      baseDiscoveryPrompt?: string;
      baseFilterPrompt?: string;
    }
  | {
      pathname: string;
      kind: 'custom_dash';
      baseFilterPrompt?: string;
    }
  | {
      pathname: string;
      kind: 'other';
    };

export type GlobalAssistantPendingClarification = {
  /** Verbatim text of the most recent assistant clarification still awaiting an answer. */
  text: string;
  /** What the assistant was asking about (e.g. dashboard scope vs. specific entity value). */
  kind?: 'entity_value' | 'entity_scope';
  /** Suggested scope choices, if the clarification was a dashboard-scope question. */
  options?: Array<'company' | 'geography' | 'role'>;
};

export type GlobalAssistantRequestContext = {
  customDash?: {
    isInCustomDashEditMode: boolean;
    hasCurrentPageItems?: boolean;
  };
  conversation?: {
    turns: GlobalAssistantConversationTurn[];
    /**
     * Compact natural-language summary of older turns that no longer
     * fit the recency window. Threaded through every downstream prompt
     * so the model can interpret short follow-ups even after a long
     * session.
     */
    summary?: string;
  };
  currentPage?: GlobalAssistantCurrentPageContext;
  /**
   * Explicit pending-clarification state for the conversational
   * clarification flow. The router can use this in addition to
   * conversation history to disambiguate short follow-ups (e.g. the
   * user replying with "company" to a scope question).
   */
  pendingClarification?: GlobalAssistantPendingClarification;
};

export type GlobalAssistantReportOffer = {
  reportType: string;
  reportName: string;
  confidence: number;
  sampleLink: string;
  description: string;
};

export type GlobalAssistantSuggestResponse = {
  assistantMessage?: string;
  existingPageMatches: GlobalAssistantSuggestMatch[];
  offerCustomPage: boolean;
  offerSingleChart: boolean;
  offerTextChart: boolean;
  offerUpdateCurrentPage: boolean;
  offerReport?: boolean;
  reportOffer?: GlobalAssistantReportOffer;
  reportOfferSuppressionReason?: string;
  /** Present when the model resolved company | geography | role (no clarification needed). */
  resolvedEntityType?: GlobalAssistantEntityType;
  /** Present when the backend says this scope is safe to reuse in follow-up calls. */
  suggestedEntityScope?: GlobalAssistantEntityType;
  /** When set, user should answer in a follow-up prompt before building a page or chart. */
  entityScopeNeedsClarification?: string;
  clarificationKind?: 'entity_value' | 'entity_scope';
  /** Actionable scope choices when clarification is required. */
  clarificationOptions?: Array<'company' | 'geography' | 'role'>;
  /** Router could not safely chart, navigate, or start custom page generation. */
  unableToFulfill?: { reason: string };
  /** Router-selected context carryover policy for follow-on calls. */
  contextPolicy?: GlobalAssistantContextPolicy;
  /** Optional backend critical-path timing diagnostics for debug timeline only. */
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantSingleChartSuccess = {
  status: 'success';
  config: unknown;
  intentSummary?: string;
  chartSubtitle?: string;
  filters?: unknown;
  resolvedEntityType: CustomDashAssistantEntityType;
  standardPagePath?: string;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantSingleChartQuestions = {
  status: 'questions';
  content: {
    questions: ClarifyingQuestion[];
    triggerReason: string;
  };
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantSingleChartError = {
  status: 'error';
  message: string;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantSingleChartEntityScope = {
  status: 'entity_scope';
  message: string;
  clarificationOptions?: Array<'company' | 'geography' | 'role'>;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantSingleChartResponse =
  | GlobalAssistantSingleChartSuccess
  | GlobalAssistantSingleChartQuestions
  | GlobalAssistantSingleChartError
  | GlobalAssistantSingleChartEntityScope;

export type GlobalAssistantTextChartSuccess = {
  status: 'success';
  config: unknown;
  intentSummary?: string;
  filters?: unknown;
  resolvedEntityType: CustomDashAssistantEntityType;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantTextChartQuestions = {
  status: 'questions';
  content: {
    questions: ClarifyingQuestion[];
    triggerReason: string;
  };
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantTextChartError = {
  status: 'error';
  message: string;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantTextChartEntityScope = {
  status: 'entity_scope';
  message: string;
  clarificationOptions?: Array<'company' | 'geography' | 'role'>;
  debugTimings?: GlobalAssistantDebugTiming[];
};

export type GlobalAssistantTextChartResponse =
  | GlobalAssistantTextChartSuccess
  | GlobalAssistantTextChartQuestions
  | GlobalAssistantTextChartError
  | GlobalAssistantTextChartEntityScope;

export type GlobalAssistantRequestBody = {
  prompt: string;
  /** Optional; from URL when user is on a company/geography/role route. */
  routeEntityHint?: GlobalAssistantEntityType;
  /** Sticky scope from prior clarification answers in this session. */
  sessionEntityScope?: GlobalAssistantEntityType;
  /** Edit surface + chat history for routing and follow-on calls. */
  context?: GlobalAssistantRequestContext;

  /** Scope already resolved by suggest; single-chart can reuse it without a second scope call. */
  suggestedEntityScope?: GlobalAssistantEntityType;
  answers?: QuestionAnswer[];
  model?: string;
  rolesTax?: string;
  skillsTax?: number;
};

type BackendGlobalAssistantEntityType = 'company' | 'geography' | 'role';

function toBackendGlobalAssistantEntityType(
  value: GlobalAssistantEntityType | undefined
): BackendGlobalAssistantEntityType | undefined {
  if (value === 'company' || value === 'geography' || value === 'role') {
    return value;
  }
  return undefined;
}

function sanitizeRequestBodyForBackend(
  body: GlobalAssistantRequestBody
): GlobalAssistantRequestBody {
  return {
    ...body,
    routeEntityHint: toBackendGlobalAssistantEntityType(body.routeEntityHint),
    sessionEntityScope: toBackendGlobalAssistantEntityType(
      body.sessionEntityScope
    ),
  };
}

export async function postGlobalAssistantSuggest(
  body: GlobalAssistantRequestBody
): Promise<GlobalAssistantSuggestResponse> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const sanitizedBody = sanitizeRequestBodyForBackend(body);
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/suggest`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(sanitizedBody),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantSuggestResponse>;
}

export async function postGlobalAssistantSingleChart(
  body: GlobalAssistantRequestBody
): Promise<GlobalAssistantSingleChartResponse> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const sanitizedBody = sanitizeRequestBodyForBackend(body);
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/single-chart`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(sanitizedBody),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantSingleChartResponse>;
}

export async function postGlobalAssistantTextChart(
  body: GlobalAssistantRequestBody
): Promise<GlobalAssistantTextChartResponse> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const sanitizedBody = sanitizeRequestBodyForBackend(body);
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/text-chart`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(sanitizedBody),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantTextChartResponse>;
}

export type GlobalAssistantReportResolvedSelection = {
  id: string;
  name: string;
  levelId: string;
  rcid?: number;
};

export type GlobalAssistantReportResolutionStatus =
  | 'resolved'
  | 'unresolved'
  | 'rejected';

export type GlobalAssistantReportParamSuggestion = {
  parameterId: string;
  displayName: string;
  searchTerm: string;
  resolvedSelection: GlobalAssistantReportResolvedSelection | null;
  resolutionStatus: GlobalAssistantReportResolutionStatus;
  resolutionReason?: string;
};

export type GlobalAssistantReportPrefillMetrics = {
  extractedCount: number;
  resolvedCount: number;
  appliedCount: number;
  skippedCount: number;
  warnings?: GlobalAssistantReportPrefillWarning[];
};

export type GlobalAssistantReportPrefillWarning = {
  parameterId: string;
  parameterDisplayName: string;
  selectionName: string;
  foundLevelId: string;
  supportedLevelIds: string[];
  reason: 'level_id_mismatch';
};

export async function postGlobalAssistantReportParams(body: {
  prompt: string;
  reportType: string;
  rolesTax?: string;
  skillsTax?: number;
}): Promise<GlobalAssistantReportParamSuggestion[]> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/report-params`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantReportParamSuggestion[]>;
}

export async function postGlobalAssistantWarmUp(): Promise<GlobalAssistantWarmUpResponse> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/warm-up`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantWarmUpResponse>;
}

export async function postGlobalAssistantSummarizeConversation(
  body: { turns: GlobalAssistantConversationTurn[] },
  signal?: AbortSignal
): Promise<{ summary: string }> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/summarize-conversation`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<{ summary: string }>;
}
